/**
 * UI Design Tokens - Apple-inspired design system
 * Consistent styling across all UI components
 */

export const UIColors = {
    // Primary Blues
    primary: 0x007AFF,
    primaryLight: 0x5AC8FA,
    primaryDark: 0x0051D5,
    
    // Semantic Colors
    success: 0x34C759,
    warning: 0xFFCC00,
    danger: 0xFF3B30,
    
    // Neutrals
    white: 0xFFFFFF,
    black: 0x000000,
    gray100: 0xF2F2F7,
    gray200: 0xE5E5EA,
    gray300: 0xD1D1D6,
    gray400: 0xC7C7CC,
    gray500: 0xAEAEB2,
    gray600: 0x8E8E93,
    gray700: 0x636366,
    gray800: 0x48484A,
    gray900: 0x3A3A3C,
    
    // Glassmorphism
    glassBg: 0x1C1C1E,
    glassBorder: 0xFFFFFF,
    
    // Accent Colors for levels
    meadow: 0x34C759,
    forest: 0x30D158,
    cave: 0xBF5AF2,
    sky: 0xFF9500,
    volcano: 0xFF453A,
    space: 0x0A84FF
};

export const UIAlpha = {
    glassBg: 0.6,
    glassBorder: 0.2,
    glassShadow: 0.3,
    overlayDark: 0.85,
    overlayLight: 0.5,
    textSecondary: 0.7,
    textTertiary: 0.5
};

export const UITypography = {
    // Font families (Inter or system fallback)
    fontFamily: '"Inter", "SF Pro Display", -apple-system, BlinkMacSystemFont, "Segoe UI", Roboto, sans-serif',
    fontFamilyMono: '"SF Mono", "Fira Code", monospace',
    
    // Sizes
    title: '64px',
    titleSmall: '48px',
    headline: '32px',
    subheadline: '24px',
    body: '18px',
    caption: '14px',
    small: '12px',
    
    // Weights
    weightLight: '300',
    weightRegular: '400',
    weightMedium: '500',
    weightSemibold: '600',
    weightBold: '700'
};

export const UISpacing = {
    xs: 4,
    sm: 8,
    md: 16,
    lg: 24,
    xl: 32,
    xxl: 48,
    xxxl: 64
};

export const UIBorderRadius = {
    sm: 8,
    md: 12,
    lg: 16,
    xl: 24,
    pill: 100
};

export const UIAnimation = {
    // Durations
    fast: 150,
    normal: 300,
    slow: 500,
    verySlow: 800,
    
    // Eases (Phaser compatible)
    easeOut: 'Power2.easeOut',
    easeIn: 'Power2.easeIn',
    easeInOut: 'Power2.easeInOut',
    bounce: 'Back.easeOut',
    elastic: 'Elastic.easeOut',
    spring: 'Cubic.easeOut'
};

// Helper to convert hex number to CSS hex string
export function hexToString(hex) {
    return '#' + hex.toString(16).padStart(6, '0');
}

// Helper to create gradient array for backgrounds
export function createGradient(color1, color2, steps = 30) {
    const gradients = [];
    for (let i = 0; i < steps; i++) {
        const t = i / steps;
        const r1 = (color1 >> 16) & 0xFF;
        const g1 = (color1 >> 8) & 0xFF;
        const b1 = color1 & 0xFF;
        const r2 = (color2 >> 16) & 0xFF;
        const g2 = (color2 >> 8) & 0xFF;
        const b2 = color2 & 0xFF;
        
        const r = Math.floor(r1 + t * (r2 - r1));
        const g = Math.floor(g1 + t * (g2 - g1));
        const b = Math.floor(b1 + t * (b2 - b1));
        
        gradients.push((r << 16) | (g << 8) | b);
    }
    return gradients;
}
